import classNames from "@lib/classNames";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";

export enum VodusSurveyType {
  sidebar = "sidebar",
  inline = "inline",
}

declare global {
  interface Window {
    vodus?: unknown;
    VodusObject?: unknown;
    __vodusScriptLoaded?: boolean;
  }
}

interface IVodusSurveyProps {
  width?: number;
  verticalPadding?: number;
  surveyType?: VodusSurveyType;
  onLoaded?: (hasContent: boolean) => void;
  placeholder?: JSX.Element;
}

const VodusSurvey: React.FunctionComponent<IVodusSurveyProps> = ({
  width = 320,
  verticalPadding = 8,
  surveyType = VodusSurveyType.sidebar,
  onLoaded,
  placeholder,
}) => {
  const bannerRef = useRef<HTMLDivElement>(null);
  const [showSidebar, setShowSidebar] = useState<boolean | null>(null);
  const [hasBannerContent, setHasBannerContent] = useState<boolean | null>(
    true
  );
  const { pathname } = useRouter();

  useEffect(() => {
    const syncBreakpoint = () => {
      setShowSidebar(window.innerWidth >= 1024);
    };

    syncBreakpoint();
    window.addEventListener("resize", syncBreakpoint);

    return () => window.removeEventListener("resize", syncBreakpoint);
  }, []);

  const shouldRenderInline =
    showSidebar !== null &&
    surveyType === VodusSurveyType.inline &&
    !showSidebar;
  const shouldRenderSidebar =
    showSidebar !== null &&
    surveyType === VodusSurveyType.sidebar &&
    showSidebar;

  useEffect(() => {
    const VODUS_SCRIPT_IDS = [
      "vodus-survey-script",
      "vodus-main-script",
      "vodus-script",
    ];
    const cleanup = () => {
      VODUS_SCRIPT_IDS.forEach((id) => {
        document.getElementById(id)?.remove();
      });

      document
        .querySelectorAll<HTMLScriptElement>('script[src*="vodus"]')
        .forEach((script) => script.remove());

      delete window.vodus;
      delete window.VodusObject;
    };

    cleanup();

    const cacheBuster = Date.now();
    const timer = setTimeout(() => {
      const script = document.createElement("script");
      script.id = "vodus-survey-script";
      script.src = `https://api.vodus.com/MediaPartners/BannerModeDirectToHTML-Lumi.js?c=${cacheBuster}`;
      script.async = true;
      document.body.appendChild(script);
    }, 50);

    return () => {
      clearTimeout(timer);
      cleanup();
    };
  }, [pathname]);

  useEffect(() => {
    const bannerElement = bannerRef.current;

    if (!bannerElement) {
      return;
    }

    const syncBannerState = () => {
      const bannerText = bannerElement.textContent?.trim().toLowerCase() ?? "";
      const hasNoSurveyMessage = bannerText.includes("no survey available");

      if (hasNoSurveyMessage) {
        setHasBannerContent(false);
        onLoaded?.(false);
        return;
      }

      const nextHasContent =
        bannerElement.childElementCount > 0 || bannerText.length > 0;

      setHasBannerContent(nextHasContent);
      onLoaded?.(nextHasContent);
    };

    syncBannerState();

    const observer = new MutationObserver(syncBannerState);

    observer.observe(bannerElement, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    return () => observer.disconnect();
  }, [onLoaded, pathname, showSidebar, surveyType]);

  if (showSidebar === null || (!shouldRenderInline && !shouldRenderSidebar)) {
    return null;
  }

  const hasContent = hasBannerContent === true;
  const wrapperStyle = hasContent ? { width } : undefined;
  const spacingClass =
    verticalPadding && hasContent
      ? surveyType === VodusSurveyType.inline
        ? `my-${verticalPadding}`
        : `mb-${verticalPadding}`
      : undefined;

  return (
    <div
      className={classNames(
        surveyType === VodusSurveyType.inline && "mx-auto",
        !hasContent && "w-full"
      )}
      style={wrapperStyle}
    >
      <div
        ref={bannerRef}
        className={classNames(
          "vodus-banner",
          spacingClass,
          !hasContent && "hidden"
        )}
      />

      {!hasContent && placeholder ? placeholder : <></>}
    </div>
  );
};

export default VodusSurvey;
